import "../PrivacyPolicy/privacy.css";

export default function TermsOfService() {
  return (
    <div className="container flex flex-column align-items-center">
      <div className="py-5 p-info m-auto">
        <h1 className="fw-semibold fs-4 text-center">Terms of Service</h1>
        <p className="text-justify">
          <strong>Last updated:</strong> February 2026
        </p>
        <p className="text-justify">
          Welcome to MovenMuse. By creating an account or using our platform, you
          agree to be bound by these Terms of Service ("Terms"). If you do not
          agree to these Terms, please do not use the Service.
        </p>
      </div>

      <div className="p-info m-auto">
        <h2 className="fs-4 fw-semibold">1. Definitions</h2>
        <ul>
          <li>
            <strong>"Platform"</strong> or <strong>"Service"</strong> refers to
            the MovenMuse website, application, and all associated services.
          </li>
          <li>
            <strong>"User"</strong>, <strong>"You"</strong>, or{" "}
            <strong>"Your"</strong> refers to any individual who accesses or uses
            the Platform.
          </li>
          <li>
            <strong>"Content"</strong> refers to any text, images, reviews,
            articles, comments, recommendations, or other materials submitted by
            Users.
          </li>
          <li>
            <strong>"We"</strong>, <strong>"Us"</strong>, or{" "}
            <strong>"Company"</strong> refers to MovenMuse.
          </li>
        </ul>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">2. Account Registration</h2>
        <p className="text-justify">
          To access certain features, you must create an account. You agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain the security of your password and account</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>
        <p className="text-justify">
          You must be at least 13 years old to create an account. By registering,
          you confirm that you meet this age requirement.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">3. User-Generated Content</h2>
        <h3 className="fs-5 fw-medium">3.1 Content Responsibility</h3>
        <p className="text-justify">
          You are solely responsible for any Content you submit to the Platform,
          including event reviews, articles, comments, goods recommendations, and
          kudos. By submitting Content, you represent and warrant that:
        </p>
        <ul>
          <li>The Content is your original work or you have the right to share it</li>
          <li>The Content is truthful and reflects your genuine personal experience</li>
          <li>The Content does not infringe any third party's intellectual property, privacy, or other rights</li>
          <li>The Content does not contain defamatory, harassing, threatening, or unlawful material</li>
        </ul>

        <h3 className="fs-5 fw-medium">3.2 Platform Role</h3>
        <p className="text-justify">
          MovenMuse is a neutral platform that hosts user-submitted content. We
          do not verify, endorse, or assume responsibility for user-submitted
          Content. Reviews, articles, and recommendations reflect individual
          experiences and opinions, not the views of MovenMuse.
        </p>

        <h3 className="fs-5 fw-medium">3.3 Content License</h3>
        <p className="text-justify">
          By submitting Content, you grant MovenMuse a non-exclusive, worldwide,
          royalty-free license to use, display, reproduce, and distribute your
          Content in connection with the Platform. You retain ownership of your
          Content and may request its removal at any time.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">4. Reviews and Defamation</h2>
        <p className="text-justify">
          When writing reviews, you agree to the following:
        </p>
        <ul>
          <li>
            Reviews must be based on your genuine personal experience at a dance
            event
          </li>
          <li>
            Naming specific individuals in relation to misconduct is only
            permitted when the incident is supported by an official report or
            police record
          </li>
          <li>
            You acknowledge that your identity and personal information may be
            disclosed in the event of a legal inquiry or defamation claim arising
            from your Content
          </li>
          <li>
            You agree to indemnify and hold MovenMuse harmless from any claims,
            damages, or legal proceedings arising from Content you submit
          </li>
        </ul>
        <p className="text-justify">
          If you have a safety concern about an event or individual, we encourage
          you to use the private "Boycott" reporting feature rather than public
          reviews.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">5. Prohibited Conduct</h2>
        <p className="text-justify">You agree not to:</p>
        <ul>
          <li>Submit false, misleading, or fraudulent Content</li>
          <li>Harass, threaten, or defame other users, event organizers, or artists</li>
          <li>Impersonate any person or entity</li>
          <li>Manipulate ratings or reviews (e.g., submitting fake reviews)</li>
          <li>Use the Platform for spam, advertising, or commercial solicitation without authorization</li>
          <li>Attempt to gain unauthorized access to the Platform or its systems</li>
          <li>Abuse the flagging or reporting system</li>
          <li>Violate any applicable laws or regulations</li>
        </ul>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">6. Content Moderation</h2>
        <p className="text-justify">
          MovenMuse reserves the right, but is not obligated, to review, edit, or
          remove any Content that violates these Terms or our Community Guidelines.
          We may suspend or terminate accounts that repeatedly violate our policies.
          Our moderation process includes:
        </p>
        <ul>
          <li>Automated filters to detect potentially harmful content at submission</li>
          <li>Community flagging system for users to report inappropriate content</li>
          <li>Admin review of flagged content with progressive enforcement (warnings, suspensions, bans)</li>
        </ul>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">7. Notice and Takedown</h2>
        <p className="text-justify">
          If you believe that content on MovenMuse references you and contains
          false, defamatory, or harmful claims:
        </p>
        <ul>
          <li>
            <strong>Account holders:</strong> Use the flag icon on the content
            and select "This review mentions me - request takedown." The content
            will be hidden immediately and placed at the top of admin review.
          </li>
          <li>
            <strong>Non-account holders:</strong> Email{" "}
            <a href="mailto:contact@movenmuse.com">contact@movenmuse.com</a>{" "}
            with the subject line "Content Removal Request," including a link or
            description of the content and your reason for requesting removal.
          </li>
        </ul>
        <p className="text-justify">
          We aim to review all takedown requests within 7 business days. Content found
          to violate our Community Guidelines or contain unsubstantiated claims
          about identifiable individuals will be removed. The person who
          submitted the request will be notified of the outcome.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">8. Account Deactivation</h2>
        <p className="text-justify">
          You may deactivate your account at any time through your account settings.
          Upon deactivation, your personal information will be anonymized and your
          profile will no longer be publicly accessible. We may also suspend or
          terminate your account if you violate these Terms, subject to our
          moderation policies.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">9. Intellectual Property</h2>
        <p className="text-justify">
          The Platform, its design, features, and original content (excluding
          user-submitted Content) are the property of MovenMuse and are protected
          by copyright, trademark, and other intellectual property laws. You may
          not copy, modify, distribute, or create derivative works from the
          Platform without our prior written consent.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">10. Disclaimer of Warranties</h2>
        <p className="text-justify">
          The Platform is provided on an "as is" and "as available" basis without
          warranties of any kind, whether express or implied. MovenMuse does not
          warrant that:
        </p>
        <ul>
          <li>The Platform will be available at all times or free from errors or interruptions</li>
          <li>Reviews, recommendations, or other user-submitted Content are accurate, complete, or reliable</li>
          <li>Event listings reflect current or correct information</li>
          <li>The Platform will meet your specific requirements or expectations</li>
        </ul>
        <p className="text-justify">
          You use the Platform at your own risk. Any reliance on Content or
          information obtained through the Platform is at your sole discretion.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">11. Limitation of Liability</h2>
        <p className="text-justify">
          To the maximum extent permitted by law, MovenMuse shall not be liable
          for any indirect, incidental, special, consequential, or punitive
          damages arising from your use of the Platform, including but not limited
          to damages arising from user-submitted Content, loss of data, or
          service interruptions.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">12. Privacy</h2>
        <p className="text-justify">
          Your use of the Platform is also governed by our{" "}
          <a href="/privacy-policy">Privacy Policy</a>, which describes how we
          collect, use, and protect your personal information. By using the
          Platform, you consent to the practices described in our Privacy Policy.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">13. Governing Law</h2>
        <p className="text-justify">
          These Terms are governed by the laws of the Province of Quebec, Canada,
          without regard to conflict of law principles. Any disputes arising from
          these Terms or your use of the Platform shall be resolved in the courts
          of Quebec.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">14. Changes to These Terms</h2>
        <p className="text-justify">
          We may update these Terms from time to time. When we do, we will update
          the "Last updated" date at the top. If the changes are significant, we
          will notify registered users. Continued use of the Platform after
          changes constitutes acceptance of the updated Terms.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">15. Severability</h2>
        <p className="text-justify">
          If any provision of these Terms is found to be invalid, illegal, or
          unenforceable by a court of competent jurisdiction, the remaining
          provisions shall continue in full force and effect. The invalid
          provision shall be modified to the minimum extent necessary to make it
          valid and enforceable while preserving its original intent.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">16. Entire Agreement</h2>
        <p className="text-justify">
          These Terms, together with our{" "}
          <a href="/privacy-policy">Privacy Policy</a>, constitute the entire
          agreement between you and MovenMuse regarding your use of the Platform.
          They supersede any prior agreements, communications, or understandings,
          whether written or oral, relating to the subject matter herein.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">17. No Waiver</h2>
        <p className="text-justify">
          The failure of MovenMuse to enforce any right or provision of these
          Terms shall not constitute a waiver of that right or provision. Any
          waiver of any provision of these Terms will be effective only if in
          writing and signed by MovenMuse.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">18. Force Majeure</h2>
        <p className="text-justify">
          MovenMuse shall not be liable for any failure or delay in performing
          its obligations under these Terms due to circumstances beyond its
          reasonable control, including but not limited to natural disasters,
          power outages, internet service disruptions, government actions, or
          other events of force majeure.
        </p>
      </div>

      <div className="py-2 p-info m-auto">
        <h2 className="fs-4 fw-semibold">19. Third-Party Links</h2>
        <p className="text-justify">
          The Platform may contain links to third-party websites or services that
          are not owned or controlled by MovenMuse. We have no control over, and
          assume no responsibility for, the content, privacy policies, or
          practices of any third-party websites or services. You access
          third-party links at your own risk.
        </p>
      </div>

      <div className="py-5 p-info m-auto">
        <h2 className="fs-4 fw-semibold text-center">Contact Us</h2>
        <p className="text-justify">
          If you have any questions about these Terms, you can contact us via
          email at contact@movenmuse.com
        </p>
      </div>
    </div>
  );
}
